import Vue from 'vue'
import config from '@/configurations/config-environment'
import $store from '@/vuex/store'
import * as localForage from 'localforage'
import _ from 'lodash'
import { ProjectService } from '@/services/DataWS/projectService'
import { BaseService } from '../baseService'

const projectService = new ProjectService()
const dataws = config.apis.dataws
const templateCacheStore = localForage.createInstance({
  name: 'templateCacheStore'
})
export class TemplateService extends BaseService {
  clearLocalCache() {
    return templateCacheStore.clear()
  }

  getFieldTemplateFromTemplateId(screenName, templateId, fieldCode) {
    const authorisation = this.getAuthorizationObject()
    const languageCode = this.getLangCodeLongFromShort(authorisation.Language)

    if (!authorisation.Username || !authorisation.Licensenumber) {
      return Promise.reject(new Error(`Cannot get template '${screenName}': no authenticated user`))
    }

    const data = {
      Authorisation: authorisation,
      LanguageCode: languageCode,
      WebserviceVersion: '1.0',
      ScreenName: screenName,
      TemplateID: templateId,
      FieldCode: fieldCode
    }

    const cacheKey = `${screenName};${templateId || ''};${languageCode}`
    return templateCacheStore
      .getItem(cacheKey)
      .then((storeResult) => {
        if (storeResult && storeResult.cacheDate && storeResult.cacheDate === new Date().toDateString()) {
          return storeResult
        }

        return Vue.http
          .post(`${dataws}ITWDataRestService_V1_0/GetTemplatesData`, data, {
            headers: {
              'Content-Type': 'application/json'
            }
          })
          .then((result) => {
            // Never cache a failed response. DataWS builds every screen in a single call, so one
            // (often transient) backend error would otherwise keep this screen broken for the rest
            // of the day, long after the cause is gone.
            if (!this.isTemplatesResponseSuccess(result)) {
              return result
            }

            result.cacheDate = new Date().toDateString()

            return templateCacheStore.setItem(cacheKey, result).then(() => {
              return result
            })
          })
      })
      .then((result) => {
        if (!this.isTemplatesResponseSuccess(result)) {
          throw new Error(`GetTemplatesData failed for screen '${screenName}': ${this.getTemplatesResponseError(result)}`)
        }

        const templates = result.data.Result.templates

        let templateID = data.TemplateID

        if (typeof templateID === 'undefined' || templateID === 'undefined') {
          templateID = '-1'
        }

        let template = _.find(templates, { id: templateID })

        if (typeof template === 'undefined' || template === 'undefined') {
          templateID = '-1'
          template = _.find(templates, { id: templateID })
        }

        if (!template) {
          throw new Error(`GetTemplatesData returned no template '${templateID}' for screen '${screenName}'`)
        }

        return template
      })
  }

  isTemplatesResponseSuccess(response) {
    return !!(response && response.data && response.data.ResultCode === 'TiComponentsCode_Succes')
  }

  // DataWS puts the actual exception message in ResultMessage, so pass it on instead of leaving
  // the caller to guess why the template could not be built.
  getTemplatesResponseError(response) {
    if (!response || !response.data) {
      return 'no response data'
    }

    return `${response.data.ResultCode} - ${response.data.ResultMessage || 'no ResultMessage'}`
  }

  getDefaultFieldTemplate(screenName, templateId) {
    return this.getFieldTemplateFromTemplateId(screenName, templateId)
  }

  getFieldTemplate(screenName, projectID, fieldCode) {
    let currentProject = $store.state.projects.find((pr) => pr.PrID === projectID)

    if (currentProject && currentProject.PrTemplateID && currentProject.PrTemplateID !== '') {
      return this.getFieldTemplateFromTemplateId(screenName, currentProject.PrTemplateID, fieldCode)
    }

    return projectService.getSingleProject(projectID).then((projectRes) => {
      if (projectRes.data && projectRes.data.ResultCode === 'Export_Succes') {
        const project = JSON.parse(projectRes.data.Files[0].FileContent).VeldData.tblProjects

        $store.state.projects.push(project)
        return this.getFieldTemplateFromTemplateId(screenName, project.PrTemplateID, fieldCode)
      }
    })
  }

  getWsMatrixCode(projectID) {
    const screenName = 'frmWatersampleDetails'
    const fieldCode = 'WsMatrixCode'
    return this.getFieldTemplate(screenName, projectID, fieldCode)
  }

  getLaSoilType(projectID) {
    const screenName = 'frmLayerDetails'
    const fieldCode = 'LaSoilType'
    return this.getFieldTemplate(screenName, projectID, fieldCode)
  }

  getFiTypeCode(projectID) {
    const screenName = 'frmFinishingDetails'
    const fieldCode = 'FiTypeCode'
    return this.getFieldTemplate(screenName, projectID, fieldCode)
  }

  getMpTypeCode(projectID) {
    const screenName = 'frmMeasurementPointDetails'
    const fieldCode = 'MpTypeCode'
    return this.getFieldTemplate(screenName, projectID, fieldCode)
  }
}
